<template>
  <div class="rental-page">
    <!-- ===== 页面头部 ===== -->
    <section class="rental-hero">
      <div class="rental-hero-overlay"></div>
      <div class="rental-hero-content">
        <h1 class="rental-hero-title">
          {{ $t("rental.title1") }}<span class="highlight"
            >{{ $t("rental.title2") }}</span
          >
        </h1>
        <p class="rental-hero-desc">{{ $t("rental.desc") }}</p>
        <div class="rental-hero-stats">
          <div class="hero-stat">
            <span class="hero-stat-number">{{ totalServers }}</span>
            <span class="hero-stat-label">{{ $t("rental.statServers") }}</span>
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-number">99.9%</span>
            <span class="hero-stat-label"
              >{{ $t("rental.statAvailability") }}</span
            >
          </div>
          <div class="hero-stat-divider"></div>
          <div class="hero-stat">
            <span class="hero-stat-number">24/7</span>
            <span class="hero-stat-label">{{ $t("rental.statSupport") }}</span>
          </div>
        </div>
      </div>

      <!-- 第一页底部 轮播 -->
      <div>

      </div>
    </section>

    <!-- ===== 筛选 & 搜索栏 ===== -->
    <section class="rental-filter-section">
      <div class="rental-filter-container">
        <div class="filter-left">
          <div class="search-box">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path
                d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              />
            </svg>
            <input
              type="text"
              v-model="searchKeyword"
              :placeholder="$t('rental.searchPlaceholder')"
              class="search-input"
              @input="handleSearch"
            />
          </div>

          <div class="filter-tags">
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'all' }"
              @click="
                activeFilter = 'all';
                handleFilter();
              "
            >
              {{ $t("rental.filterAll") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'hot' }"
              @click="
                activeFilter = 'hot';
                handleFilter();
              "
            >
              {{ $t("rental.filterHot") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'recommend' }"
              @click="
                activeFilter = 'recommend';
                handleFilter();
              "
            >
              {{ $t("rental.filterRecommend") }}
            </button>
            <button
              class="filter-tag"
              :class="{ active: activeFilter === 'new' }"
              @click="
                activeFilter = 'new';
                handleFilter();
              "
            >
              {{ $t("rental.filterNew") }}
            </button>
          </div>
        </div>

        <div class="filter-right">
          <select v-model="sortBy" class="sort-select" @change="handleSort">
            <option value="remaining-asc">{{ $t("rental.sortRemainingAsc") }}</option>
            <option value="remaining-desc">{{ $t("rental.sortRemainingDesc") }}</option>
            <option value="value-asc">{{ $t("rental.sortValueAsc") }}</option>
            <option value="value-desc">{{ $t("rental.sortValueDesc") }}</option>
            <option value="start-asc">{{ $t("rental.sortStartAsc") }}</option>
            <option value="start-desc">{{ $t("rental.sortStartDesc") }}</option>
            <option value="end-asc">{{ $t("rental.sortEndAsc") }}</option>
            <option value="end-desc">{{ $t("rental.sortEndDesc") }}</option>
          </select>

          <div class="view-toggle">
            <button
              class="view-btn"
              :class="{ active: viewMode === 'grid' }"
              @click="viewMode = 'grid'"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M3 3h8v8H3zm0 10h8v8H3zm10-10h8v8h-8zm0 10h8v8h-8z" />
              </svg>
            </button>
            <button
              class="view-btn"
              :class="{ active: viewMode === 'list' }"
              @click="viewMode = 'list'"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path d="M3 13h18v2H3zm0-6h18v2H3zm0 12h18v2H3z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 服务器列表 ===== -->
    <section class="rental-list-section">
      <div class="rental-list-container">
        <div class="rental-result-count">
          {{ $t("rental.resultCount", { count: totalServers }) }}
        </div>

        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <span>{{ $t("rental.loading") }}</span>
        </div>

        <div v-else-if="filteredServers.length === 0" class="empty-state">
          <div class="empty-icon">🔍</div>
          <p>{{ $t("rental.empty") }}</p>
        </div>

        <!-- ===== 网格视图 ===== -->
        <div v-else-if="viewMode === 'grid'" class="rental-grid">
          <div
            class="rental-card rental-card-highlight"
            v-for="server in filteredServers"
            :key="server.id"
          >
            <div class="rental-card-badges">
              <div>
                <span
                  v-if="server.highlight > 0"
                  :class="['badge', getHighlightClass(server.highlight)]"
                  >{{ getHighlightText(server.highlight) }}</span
                >
              </div>
              <!-- <span v-if="server.status === 2" class="badge badge-available"
                >✅ 可租赁</span
              > -->
              <!-- <span v-else class="badge badge-sold">⏳ 已满</span> -->
              <div>
                <img
                  v-if="server.country_img"
                  :src="server.country_img"
                  alt="国家"
                  class="country-flag"
                />
              </div>
            </div>

            <div class="-rental-card-header rental-card-gpu">
              <div class="rental-card-icon">
                <ServerIconIsometric size="sm" />
              </div>
              <div class="rental-card-title-wrap">
                <h3 class="rental-card-name">{{ server.title }}</h3>
                <!-- <span class="rental-card-spec">{{
                  getSpecText(server.spec)
                }}</span> -->
              </div>
            </div>

            <!-- <div class="rental-card-gpu">
              <span class="gpu-model">{{
                truncateText(server.gpu || server.title, 40)
              }}</span>
              <span class="gpu-detail"
                >{{ truncateText(server.vram || "--", 20) }} ·
                {{ truncateText(server.ssd || "--", 20) }}</span
              >
            </div> -->

            <div class="rental-card-pricing">
              <div class="price-item">
                <span class="price-label">{{ $t("rental.totalValue") }}</span>
                <span class="price-value-item"
                  >${{ formatValue(server.value) }}</span
                >
              </div>
              <!-- <div class="price-divider"></div>
              <div class="price-item">
                <span class="price-label">国家</span>
                <span class="price-value country-name">{{
                  server.country_name || "--"
                }}</span>
              </div> -->
              <div class="price-divider"></div>
              <div class="price-item chart-item">
                <span class="price-label">{{ $t("rental.incomeTrend") }}</span>
                <div :ref="'chartRef' + server.id" class="chart-box"></div>
              </div>
            </div>

            <div class="rental-value-progress">
              <div class="value-header">
                <!-- <div class="value-info">
                  <span class="value-label">价值进度</span>
                </div> -->
                <span class="value-progress-text">
                  {{ calcValueProgress(server) }}%
                </span>
              </div>

              <div class="progress-track">
                <div
                  class="progress-bar"
                  :style="`width: ${calcValueProgress(server)}%`"
                >
                  <span class="progress-glow"></span>
                </div>
              </div>

              <div class="value-footer">
                <!-- <span class="value-remaining">
                  剩余
                  <strong>${{ calcValueAmount(server) }}</strong>
                </span> -->
                <!-- <span
                  class="value-status"
                  :class="
                    getValueStatus(server.surplus || 0, server.value || 100000)
                  "
                >
                  {{
                    getValueStatusText(
                      server.surplus || 0,
                      server.value || 100000,
                    )
                  }}
                </span> -->
              </div>
            </div>

            <div class="rental-activity-period">
              <div class="period-header">
                <span class="period-label">{{ $t("rental.activityPeriod") }}</span>
                <span class="period-date">
                  {{ formatDate(server.begin_time) }} ~
                  {{ formatDate(server.end_time) }}
                </span>
              </div>
              <div class="period-footer">
                <span class="period-remaining">
                  {{ $t("rental.remaining") }}
                  <strong>{{ calcRemainingDays(server.end_time) }}</strong>
                  {{ $t("rental.days") }}
                </span>
                <span
                  class="period-status"
                  :class="getPeriodStatus(server.begin_time, server.end_time)"
                >
                  {{ getPeriodStatusText(server.begin_time, server.end_time) }}
                </span>
              </div>
            </div>

            <div class="rental-card-specs">
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.memory") }}</span>
                <span class="spec-value">{{
                  truncateText(server.ram || "--", 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.storage") }}</span>
                <span class="spec-value">{{
                  truncateText(server.ssd || "--", 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.bandwidth") }}</span>
                <span class="spec-value">{{
                  truncateText(server.bandwidth || "--", 18)
                }}</span>
              </div>
              <div class="spec-item">
                <span class="spec-label">{{ $t("rental.status") }}</span>
                <span
                  class="spec-value"
                  :class="
                    getServerStatus(server) === 'active' &&
                    server.status === 2
                      ? 'available-yes'
                      : 'available-no'
                  "
                >
                  {{
                    getServerStatus(server) === 'ended'
                      ? $t("rental.statusEnded")
                      : getServerStatus(server) === 'pending'
                        ? $t("rental.btnNotStart")
                        : server.status === 2
                          ? $t("rental.available")
                          : $t("rental.full")
                  }}
                </span>
              </div>
            </div>

            <!-- 倒计时 -->
            <div
              v-if="getServerStatus(server) === 'pending'"
              class="countdown-box"
            >
              <span class="countdown-label">{{ $t("rental.countdown") }}</span>
              <span class="countdown-time">{{ formatCountdown(server) }}</span>
            </div>

            <button
              class="btn btn-rental-card"
              :class="{ 'btn-full': calcValueProgress(server) >= 100 }"
              :disabled="
                getServerStatus(server) !== 'active' ||
                calcValueProgress(server) >= 100
              "
              @click="handleRental(server)"
            >
              {{
                calcValueProgress(server) >= 100
                  ? $t("rental.btnFull")
                  : getServerStatus(server) === "active"
                    ? $t("rental.btnRent")
                    : getServerStatus(server) === "pending"
                      ? $t("rental.btnWait")
                      : $t("rental.btnEnded")
              }}
              <span
                v-if="
                  getServerStatus(server) === 'active' &&
                  calcValueProgress(server) < 100
                "
                class="btn-arrow"
                >→</span
              >
            </button>
          </div>
        </div>

        <!-- ===== 列表视图 ===== -->
        <div v-else class="rental-list">
          <div
            class="rental-list-item"
            v-for="server in filteredServers"
            :key="server.id"
          >
            <div class="list-item-main">
              <div class="list-item-icon">
                <ServerIconIsometric size="sm" />
              </div>
              <div class="list-item-info">
                <div class="list-item-header">
                  <h4>{{ truncateText(server.title, 20) }}</h4>
                  <span class="list-item-spec">{{
                    getSpecText(server.spec)
                  }}</span>
                  <span
                    v-if="server.highlight === 1 || server.highlight === 2"
                    class="badge badge-hot"
                    >{{ $t("rental.tagHot") }}</span
                  >
                </div>
                <div class="list-item-gpu">
                  {{ truncateText(server.gpu || server.title, 50) }}
                </div>
              </div>
              <div class="list-item-pricing">
                <div class="list-price">
                  <span class="list-price-label">{{ $t("rental.value") }}</span>
                  <span class="list-price-value"
                    >${{ formatValue(server.value) }}</span
                  >
                </div>
                <div class="list-price">
                  <!-- <span class="list-price-label">国家</span>
                  <span class="list-price-value">{{
                    server.country_name || "--"
                  }}</span> -->
                  <img
                    v-if="server.country_img"
                    :src="server.country_img"
                    alt="国家"
                    class="country-flag"
                  />
                </div>
              </div>
              <div class="list-item-status">
                <span
                  :class="
                    getServerStatus(server) === 'active' &&
                    calcValueProgress(server) < 100
                      ? 'status-available'
                      : 'status-unavailable'
                  "
                >
                  {{
                    calcValueProgress(server) >= 100
                      ? $t("rental.btnFull")
                      : getServerStatus(server) === "active"
                        ? $t("rental.available")
                        : getServerStatus(server) === "pending"
                          ? $t("rental.btnNotStart")
                          : $t("rental.btnEnded")
                  }}
                </span>
              </div>
              <div
                v-if="getServerStatus(server) === 'pending'"
                class="countdown-box countdown-list"
              >
                <span class="countdown-label">{{ $t("rental.countdown") }}</span>
                <span class="countdown-time">{{
                  formatCountdown(server)
                }}</span>
              </div>
              <button
                class="btn btn-rental-list"
                :class="{ 'btn-full': calcValueProgress(server) >= 100 }"
                :disabled="
                  getServerStatus(server) !== 'active' ||
                  calcValueProgress(server) >= 100
                "
                @click="handleRental(server)"
              >
                {{
                  calcValueProgress(server) >= 100
                    ? $t("rental.btnFull")
                    : getServerStatus(server) === "active"
                      ? $t("rental.btnRentList")
                      : getServerStatus(server) === "pending"
                        ? $t("rental.btnWait")
                        : $t("rental.btnEnded")
                }}
              </button>
            </div>
          </div>
        </div>

        <!-- ===== 分页 ===== -->
        <div class="rental-pagination" v-if="totalServers > 0">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            {{ $t("rental.prev") }}
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="changePage(currentPage + 1)"
          >
            {{ $t("rental.next") }}
          </button>
          <select
            class="page-size-select"
            v-model="pageSize"
            @change="changePage(1)"
          >
            <option :value="6">{{ $t("rental.perPage", { count: 6 }) }}</option>
            <option :value="9">{{ $t("rental.perPage", { count: 9 }) }}</option>
            <option :value="12">{{ $t("rental.perPage", { count: 12 }) }}</option>
            <option :value="18">{{ $t("rental.perPage", { count: 18 }) }}</option>
          </select>
        </div>
      </div>
    </section>

    <!-- ===== 租赁流程 ===== -->
    <section class="rental-process">
      <div class="process-container">
        <div class="section-header">
          <span class="section-badge">{{ $t("rental.processBadge") }}</span>
          <h2 class="section-title">
            {{ $t("rental.processTitle1") }}<span class="highlight"
              >{{ $t("rental.processTitle2") }}</span
            >
          </h2>
        </div>
        <div class="process-steps">
          <div class="process-step">
            <div class="step-number">1</div>
            <div class="step-icon">{{ $t("rental.step1Icon") }}</div>
            <h4>{{ $t("rental.step1Title") }}</h4>
            <p>{{ $t("rental.step1Desc") }}</p>
          </div>
          <div class="process-arrow">→</div>
          <div class="process-step">
            <div class="step-number">2</div>
            <div class="step-icon">{{ $t("rental.step2Icon") }}</div>
            <h4>{{ $t("rental.step2Title") }}</h4>
            <p>{{ $t("rental.step2Desc") }}</p>
          </div>
          <div class="process-arrow">→</div>
          <div class="process-step">
            <div class="step-number">3</div>
            <div class="step-icon">{{ $t("rental.step3Icon") }}</div>
            <h4>{{ $t("rental.step3Title") }}</h4>
            <p>{{ $t("rental.step3Desc") }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA 区 ===== -->
    <section class="rental-cta">
      <div class="rental-cta-bg">
        <div class="rental-cta-orb-1"></div>
        <div class="rental-cta-orb-2"></div>
      </div>
      <div class="rental-cta-content">
        <h2>{{ $t("rental.ctaTitle") }}</h2>
        <p>{{ $t("rental.ctaDesc") }}</p>
        <div class="rental-cta-actions">
          <button class="btn btn-cta-primary">{{ $t("rental.ctaContact") }}</button>
          <button class="btn btn-cta-secondary">{{ $t("rental.ctaFaq") }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import * as echarts from "echarts";

const ServerSpecMap = {
  1: "rental.spec1",
  2: "rental.spec2",
  3: "rental.spec3",
  4: "rental.spec4",
};

const ServerHighlightMap = {
  1: {
    text: "rental.tagHot",
    class: "badge-hot",
  },
  2: {
    text: "rental.tagRecommend",
    class: "badge-recommend",
  },
  3: {
    text: "rental.tagNew",
    class: "badge-available",
  },
};

import ServerIconIsometric from "@/components/server/ServerIconIsometric.vue";
export default {
  name: "RentalPage",
  components: {
    ServerIconIsometric,
  },
  data() {
    return {
      allServers: [],
      totalServers: 0,
      loading: false,
      currentPage: 1,
      pageSize: 9,
      searchKeyword: "",
      activeFilter: "all",
      sortBy: "remaining-asc",
      viewMode: "grid",

      chartInstances: {},
      now: Date.now(),
      countdownTimer: null,
    };
  },
  computed: {
    filteredServers() {
      let result = [...this.allServers];

      if (this.searchKeyword.trim()) {
        const keyword = this.searchKeyword.toLowerCase();
        result = result.filter(
          (s) =>
            s.title?.toLowerCase().includes(keyword) ||
            s.gpu?.toLowerCase().includes(keyword) ||
            s.label?.toLowerCase().includes(keyword),
        );
      }

      switch (this.activeFilter) {
        case "hot":
          result = result.filter((s) => s.highlight === 1);
          break;
        case "recommend":
          result = result.filter((s) => s.highlight === 2);
          break;
        case "new":
          result = result.filter((s) => s.highlight === 3);
          break;
        case "spec-1":
          result = result.filter((s) => s.spec === 1);
          break;
        case "spec-2":
          result = result.filter((s) => s.spec === 2);
          break;
        case "spec-3":
          result = result.filter((s) => s.spec === 3);
          break;
        case "spec-4":
          result = result.filter((s) => s.spec === 4);
          break;
        default:
          break;
      }

      switch (this.sortBy) {
        case "remaining-asc":
          result.sort(
            (a, b) => this.calcValueAmount(a) - this.calcValueAmount(b),
          );
          break;
        case "remaining-desc":
          result.sort(
            (a, b) => this.calcValueAmount(b) - this.calcValueAmount(a),
          );
          break;
        case "value-asc":
          result.sort((a, b) => (a.value || 0) - (b.value || 0));
          break;
        case "value-desc":
          result.sort((a, b) => (b.value || 0) - (a.value || 0));
          break;
        case "start-asc":
          result.sort(
            (a, b) => new Date(a.begin_time) - new Date(b.begin_time),
          );
          break;
        case "start-desc":
          result.sort(
            (a, b) => new Date(b.begin_time) - new Date(a.begin_time),
          );
          break;
        case "end-asc":
          result.sort((a, b) => new Date(a.end_time) - new Date(b.end_time));
          break;
        case "end-desc":
          result.sort((a, b) => new Date(b.end_time) - new Date(a.end_time));
          break;
        default:
          break;
      }

      return result;
    },
    totalPages() {
      return Math.ceil(this.totalServers / this.pageSize);
    },
  },
  mounted() {
    this.getServerList();
    // 启动倒计时定时器，每秒更新
    this.countdownTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  updated() {
    // 倒计时每秒更新 now 会触发 updated，但不需要重新初始化图表
    // 图表初始化在 getServerList 数据加载完成后调用
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    Object.values(this.chartInstances).forEach((chart) => {
      if (chart) chart.dispose();
    });
    this.chartInstances = {};
  },
  methods: {
    // ===== 截断文本 =====
    truncateText(text, maxLength) {
      if (!text) return "--";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    // ===== 获取服务器列表 =====
    getServerList() {
      this.loading = true;
      this.$api.server
        .getServerList({
          page: this.currentPage,
          size: this.pageSize,
        })
        .then((res) => {
          if (res.code === 0 && res.data) {
            const data = res.data.data || [];
            this.allServers = data.map((item) => ({
              ...item,
              surplus:
                item.surplus || Math.floor(Math.random() * item.value * 0.7),
              dailyYield: (Math.random() * 2 + 0.3).toFixed(1) + "%",
            }));
            this.totalServers = res.data.total || data.length;
          } else {
            this.allServers = [];
            this.totalServers = 0;
          }
        })
        .catch((err) => {
          console.error("获取服务器列表失败:", err);
          this.allServers = [];
          this.totalServers = 0;
        })
        .finally(() => {
          this.loading = false;
          // 数据加载完成后，等待 DOM 渲染完成再初始化图表
          this.$nextTick(() => {
            this.initCharts();
          });
        });
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.getServerList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleSearch() {},
    handleFilter() {},
    handleSort() {},

    getSpecText(spec) {
      return this.$t(ServerSpecMap[spec] || "");
    },
    getHighlightText(highlight) {
      return this.$t(ServerHighlightMap[highlight]?.text || "");
    },
    getHighlightClass(highlight) {
      return ServerHighlightMap[highlight]?.class || "";
    },

    formatDate(dateStr) {
      if (!dateStr) return "--";
      const date = new Date(dateStr);
      return date.toISOString().split("T")[0];
    },

    formatValue(value) {
      if (!value) return "0";
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + "w";
      }
      return value.toLocaleString();
    },

    calcRemainingDays(end) {
      if (!end) return 0;
      const now = new Date().getTime();
      const endTime = new Date(end).getTime();
      const diff = endTime - now;
      if (diff <= 0) return 0;
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    },

    calcValueProgress(item) {
      const rate = (1 - (item.surplus - item.sale)) * 100;
      if (!rate) return 0;
      return Math.min(Math.round(rate), 100);
    },
    calcValueAmount(item) {
      const amount = (item.surplus - item.sale) * item.value;
      if (!amount) return 0;
      return Math.max(Math.round(amount), 0);
    },

    // getValueStatus(used, total) {
    //   if (!used || !total) return "pending";
    //   const progress = used / total;
    //   if (progress >= 0.9) return "high";
    //   if (progress >= 0.6) return "medium";
    //   if (progress >= 0.3) return "low";
    //   return "pending";
    // },

    // getValueStatusText(used, total) {
    //   const status = this.getValueStatus(used, total);
    //   const map = {
    //     high: "🔥 即将售罄",
    //     medium: "⚡ 热销中",
    //     low: "📈 持续增长",
    //     pending: "🌟 新品上市",
    //   };
    //   return map[status] || "热销中";
    // },

    getPeriodStatus(start, end) {
      if (!start || !end) return "active";
      const now = new Date().getTime();
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();
      if (now < startTime) return "pending";
      if (now > endTime) return "ended";
      return "active";
    },

    getPeriodStatusText(start, end) {
      const status = this.getPeriodStatus(start, end);
      const map = {
        active: this.$t("rental.statusActive"),
        pending: this.$t("rental.statusPending"),
        ended: this.$t("rental.statusEnded"),
      };
      return map[status] || this.$t("rental.statusActive");
    },

    // ===== 判断服务器状态 =====
    // pending: 未开始（倒计时中）
    // active: 进行中（可租赁）
    // ended: 已结束（不可租赁）
    getServerStatus(server) {
      const now = this.now;
      const beginTime = server.begin_time
        ? new Date(server.begin_time).getTime()
        : null;
      const endTime = server.end_time
        ? new Date(server.end_time).getTime()
        : null;

      // 没有时间信息，默认按 status 判断
      if (!beginTime && !endTime) {
        return server.status == 2 ? "active" : "ended";
      }

      // 未到开始时间
      if (beginTime && now < beginTime) {
        return "pending";
      }

      // 已过结束时间
      if (endTime && now > endTime) {
        return "ended";
      }

      return "active";
    },

    // ===== 格式化倒计时 =====
    formatCountdown(server) {
      const beginTime = server.begin_time
        ? new Date(server.begin_time).getTime()
        : null;
      if (!beginTime) return "";

      const diff = beginTime - this.now;
      if (diff <= 0) return "00:00:00";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (n) => String(n).padStart(2, "0");

      if (days > 0) {
        return `${days}${this.$t("rental.days")} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
      return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    },

    initCharts() {
      this.filteredServers.forEach((server) => {
        const refKey = `chartRef${server.id}`;
        const dom = this.$refs[refKey];
        if (!dom) return;

        if (this.chartInstances[server.id]) {
          this.chartInstances[server.id].dispose();
          delete this.chartInstances[server.id];
        }

        const chartDom = Array.isArray(dom) ? dom[0] : dom;
        if (!chartDom) return;

        const chart = echarts.init(chartDom);
        this.chartInstances[server.id] = chart;

        // 优先使用服务器返回的 income 数据
        const trendData = [];
        const xData = [];
        const incomeData = server.income;
        if (Array.isArray(incomeData) && incomeData.length > 0) {
          // 使用全部数据，按实际数据量显示
          incomeData.forEach((item) => {
            xData.push(item.date ? item.date.slice(5) : "--");
            trendData.push(parseFloat(item.amount) || 0);
          });
        } else {
          // 没有数据，生成7个0
          const defaultLabels = [
            this.$t("rental.monday"),
            this.$t("rental.tuesday"),
            this.$t("rental.wednesday"),
            this.$t("rental.thursday"),
            this.$t("rental.friday"),
            this.$t("rental.saturday"),
            this.$t("rental.sunday"),
          ];
          for (let i = 0; i < 7; i++) {
            xData.push(defaultLabels[i]);
            trendData.push(0);
          }
        }

        // 计算 y 轴范围，让折线振幅更大
        const dataMin = Math.min(...trendData);
        const dataMax = Math.max(...trendData);
        const dataRange = dataMax - dataMin;
        // 上下各留 10% 的余量，让折线尽量填满图表
        const yMin = dataRange > 0 ? dataMin - dataRange * 0.1 : dataMin - 1;
        const yMax = dataRange > 0 ? dataMax + dataRange * 0.1 : dataMax + 1;

        const option = {
          tooltip: {
            trigger: "axis",
            formatter: (params) => {
              const p = params[0];
              return `${p.axisValue}<br/>${this.$t("rental.income")}: <strong>$${p.value.toFixed(
                0,
              )}</strong>`;
            },
            backgroundColor: "rgba(10, 14, 26, 0.85)",
            borderColor: "rgba(0, 180, 255, 0.2)",
            borderWidth: 1,
            textStyle: { color: "#fff", fontSize: 11 },
          },
          grid: {
            left: 2,
            right: 2,
            top: 0,
            bottom: 0,
          },
          xAxis: {
            type: "category",
            data: xData,
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
              color: "rgba(255,255,255,0.2)",
              fontSize: 9,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            min: yMin,
            max: yMax,
            splitLine: { show: false },
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          series: [
            {
              type: "line",
              smooth: true,
              data: trendData,
              symbol: "circle",
              symbolSize: 4,
              lineStyle: {
                color: "#00b4ff",
                width: 2,
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "rgba(0, 180, 255, 0.3)" },
                  { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
                ]),
              },
              itemStyle: {
                color: "#00b4ff",
              },
            },
          ],
        };

        chart.setOption(option, true);
      });
    },

    handleRental(server) {
      this.$router.push({
        name: "RentalDetail",
        query: { code: server.code },
      });
    },
  },
};
</script>

<style scoped>
/* ===== 基础重置 & 全局 ===== */
.rental-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #0a0e1a;
  color: #fff;
  overflow-x: hidden;
}

/* ===== 英雄区 ===== */
.rental-hero {
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 60px 20px 50px;
  background: linear-gradient(135deg, #0a0e1a, #0d1a2b);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.rental-hero-overlay {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 70% 30%,
    rgba(0, 180, 255, 0.05),
    transparent 70%
  );
  pointer-events: none;
}

.rental-hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 700px;
}

.rental-hero-title {
  font-size: 48px;
  font-weight: 900;
  margin: 0 0 12px 0;
  line-height: 1.1;
}

.rental-hero-desc {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.8;
  margin: 0 0 28px 0;
}

.highlight {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.rental-hero-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-stat-number {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
}

.hero-stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.06);
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  background: rgba(0, 180, 255, 0.08);
  border: 1px solid rgba(0, 180, 255, 0.1);
  font-size: 13px;
  color: #00b4ff;
  margin-bottom: 12px;
}

.section-title {
  font-size: 40px;
  font-weight: 800;
  margin: 0;
}

/* ===== 筛选栏 ===== */
.rental-filter-section {
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(20px);
}

.rental-filter-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 8px 16px;
  min-width: 200px;
  flex: 0 1 260px;
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.search-box:focus-within {
  border-color: rgba(0, 180, 255, 0.3);
  box-shadow: 0 0 0 4px rgba(0, 100, 255, 0.06);
  color: #00b4ff;
}

.search-input {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 14px;
  outline: none;
  width: 100%;
  font-family: inherit;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.2);
}

.filter-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-tag {
  padding: 5px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.filter-tag:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.12);
}

.filter-tag.active {
  background: rgba(0, 180, 255, 0.12);
  border-color: rgba(0, 180, 255, 0.2);
  color: #00b4ff;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.sort-select {
  padding: 7px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  font-family: inherit;
  min-width: 100px;
}

.sort-select option {
  background: #1a2332;
  color: #fff;
}

.view-toggle {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 4px;
}

.view-btn {
  padding: 6px 10px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.view-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}

.view-btn.active {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

/* ===== 服务器列表 ===== */
.rental-list-section {
  padding: 24px 20px 60px;
  max-width: 1200px;
  margin: 0 auto;
}

.rental-result-count {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 20px;
}

.rental-result-count span {
  color: #fff;
  font-weight: 600;
}

/* ===== 加载 & 空状态 ===== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
  color: rgba(255, 255, 255, 0.3);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.06);
  border-top-color: #00b4ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 12px;
  color: rgba(255, 255, 255, 0.3);
}

.empty-icon {
  font-size: 48px;
}

/* ===== 网格视图 ===== */
.rental-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.rental-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 18px 16px;
  transition: all 0.4s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.rental-card:hover {
  transform: translateY(-6px);
  border-color: rgba(0, 180, 255, 0.15);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.06);
}

.rental-card-highlight {
  border-color: rgba(255, 215, 0, 0.12);
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.03),
    rgba(0, 180, 255, 0.03)
  );
}

.rental-card-highlight:hover {
  border-color: rgba(255, 215, 0, 0.25);
}

/* ===== 标签 ===== */
.rental-card-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-hot {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.badge-available {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
}

.badge-sold {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
}

.badge-recommend {
  background: rgb(255 235 59 / 11%);
  color: #ffeb3b;
}
.country-flag {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

/* ===== 头部 ===== */
.rental-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  min-width: 0;
}

.rental-card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
  overflow: hidden;
}

.rental-card-title-wrap {
  min-width: 0;
  flex: 1;
}

.rental-card-name {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rental-card-spec {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  display: block;
}

.country-name {
  font-size: 15px !important;
}

/* ===== GPU ===== */
.rental-card-gpu {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  min-width: 0;
  display: flex;
  gap: 15px;
  align-items: center;
}

.gpu-model {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gpu-detail {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== 价格 ===== */
.rental-card-pricing {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  margin-bottom: 12px;
}

.price-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  flex-shrink: 1;
  min-width: 0;
}

.price-item:last-child {
  flex: 1;
  min-width: 0;
}

.price-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.price-value-item {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70px;
}

.price-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.chart-item {
  flex: 1;
  min-width: 0;
}

.chart-box {
  width: 100%;
  height: 90px;
  min-width: 50px;
}

/* ===== 价值进度条 ===== */
.rental-value-progress {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.value-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.value-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.value-progress-text {
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: visible;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #00b4ff, #7c3aed);
  position: relative;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-glow {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background: radial-gradient(circle, rgba(0, 180, 255, 0.4), transparent 70%);
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translateY(-50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.3);
  }
}

.value-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.value-remaining {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.value-remaining strong {
  color: #ffd866;
  font-weight: 700;
}

.value-status {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 10px;
  white-space: nowrap;
}

.value-status.high {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
}

.value-status.medium {
  background: rgba(255, 152, 0, 0.12);
  color: #ff9800;
}

.value-status.low {
  background: rgba(0, 180, 255, 0.12);
  color: #00b4ff;
}

.value-status.pending {
  background: rgba(0, 201, 81, 0.12);
  color: #00c951;
}

/* ===== 活动周期 ===== */
.rental-activity-period {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.period-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: nowrap;
  min-width: 0;
}

.period-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.period-date {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  font-family: "SF Mono", "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.period-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.period-remaining {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

.period-remaining strong {
  color: #ffd866;
  font-weight: 700;
}

.period-status {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 10px;
  white-space: nowrap;
}

.period-status.active {
  background: rgba(0, 201, 81, 0.08);
  color: #00c951;
}

.period-status.pending {
  background: rgba(255, 152, 0, 0.08);
  color: #ff9800;
}

.period-status.ended {
  background: rgba(255, 107, 107, 0.08);
  color: #ff6b6b;
}

/* ===== 规格 ===== */
.rental-card-specs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 10px;
  margin-bottom: 12px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
  min-width: 0;
}

.spec-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
}

.spec-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
  text-align: right;
}

.available-yes {
  color: #00c951;
}

.available-no {
  color: #ff6b6b;
}

/* ===== 按钮 ===== */
.btn-rental-card {
  width: 100%;
  padding: 10px 0;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
}

.btn-rental-card:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 255, 0.3);
}

.btn-rental-card:disabled {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.2);
  cursor: not-allowed;
}

/* ===== 已满额按钮（红色系） ===== */
.btn-rental-card.btn-full,
.btn-rental-list.btn-full {
  background: rgba(255, 107, 107, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.2);
  cursor: not-allowed;
}

/* ===== 倒计时 ===== */
.countdown-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.15);
}

.countdown-label {
  font-size: 12px;
  color: rgba(255, 152, 0, 0.7);
  font-weight: 500;
  white-space: nowrap;
}

.countdown-time {
  font-size: 15px;
  font-weight: 800;
  color: #ff9800;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* 列表视图倒计时 */
.countdown-list {
  margin-bottom: 0;
  padding: 4px 10px;
  flex-shrink: 0;
}

.countdown-list .countdown-time {
  font-size: 13px;
}

.btn-arrow {
  transition: transform 0.3s ease;
}

.btn-rental-card:hover:not(:disabled) .btn-arrow {
  transform: translateX(4px);
}

/* ===== 列表视图 ===== */
.rental-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rental-list-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 14px 18px;
  transition: all 0.3s ease;
}

.rental-list-item:hover {
  border-color: rgba(0, 180, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

.list-item-main {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
}

.list-item-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00b4ff;
  flex-shrink: 0;
  overflow: hidden;
}

.list-item-info {
  flex: 1;
  min-width: 0;
}

.list-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.list-item-header h4 {
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.list-item-spec {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.list-item-gpu {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.list-item-pricing {
  display: flex;
  gap: 20px;
  flex-shrink: 0;
}

.list-price {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.list-price-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  text-transform: uppercase;
}

.list-price-value {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.list-item-status {
  flex-shrink: 0;
}

.status-available {
  color: #00c951;
  font-size: 14px;
  font-weight: 600;
}

.status-unavailable {
  color: #ff6b6b;
  font-size: 14px;
  font-weight: 600;
}

.btn-rental-list {
  padding: 7px 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  flex-shrink: 0;
}

.btn-rental-list:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 100, 255, 0.25);
}

.btn-rental-list:disabled {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.2);
  cursor: not-allowed;
}

/* ===== 分页 ===== */
.rental-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  flex-wrap: wrap;
}

.page-btn {
  padding: 8px 18px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
}

.page-size-select {
  padding: 7px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  outline: none;
  cursor: pointer;
  font-family: inherit;
}

.page-size-select option {
  background: #1a2332;
  color: #fff;
}

/* ===== 租赁流程 ===== */
.rental-process {
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.01);
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.process-container {
  max-width: 1000px;
  margin: 0 auto;
}

.process-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.process-step {
  flex: 1;
  text-align: center;
  padding: 28px 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  position: relative;
  transition: all 0.3s ease;
}

.process-step:hover {
  border-color: rgba(0, 180, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
}

.step-number {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.process-step h4 {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.process-step p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.6;
  margin: 0;
}

.process-arrow {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

/* ===== CTA ===== */
.rental-cta {
  padding: 60px 20px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0a0e1a, #0d1a2b);
  border-top: 1px solid rgba(255, 255, 255, 0.03);
}

.rental-cta-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.rental-cta-orb-1 {
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: #00b4ff;
  filter: blur(100px);
  opacity: 0.06;
  top: -100px;
  right: -50px;
}

.rental-cta-orb-2 {
  position: absolute;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  background: #7c3aed;
  filter: blur(80px);
  opacity: 0.08;
  bottom: -80px;
  left: -30px;
}

.rental-cta-content {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
}

.rental-cta-content h2 {
  font-size: 32px;
  font-weight: 800;
  margin: 0 0 10px 0;
}

.rental-cta-content p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 28px 0;
}

.rental-cta-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}

.btn-cta-primary {
  padding: 12px 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-cta-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(0, 100, 255, 0.3);
}

.btn-cta-secondary {
  padding: 12px 36px;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 15px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-cta-secondary:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .rental-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .rental-filter-container {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-left {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    flex: unset;
    width: 100%;
  }

  .filter-tags {
    justify-content: center;
  }

  .filter-right {
    justify-content: center;
  }

  .process-steps {
    flex-direction: column;
  }

  .process-arrow {
    transform: rotate(90deg);
  }

  .rental-hero-title {
    font-size: 36px;
  }

  .list-item-gpu {
    max-width: 200px;
  }
}

@media (max-width: 768px) {
  .rental-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }

  .list-item-main {
    flex-wrap: wrap;
    gap: 10px;
  }

  .list-item-pricing {
    gap: 14px;
  }

  .list-price-value {
    font-size: 14px;
  }

  .rental-hero-stats {
    gap: 16px;
    flex-wrap: wrap;
  }

  .hero-stat-divider {
    display: none;
  }

  .section-title {
    font-size: 28px;
  }

  .rental-cta-content h2 {
    font-size: 24px;
  }

  .rental-card-pricing {
    flex-wrap: wrap;
    gap: 6px;
  }

  .price-divider {
    display: none;
  }

  .chart-box {
    height: 60px;
  }

  .list-item-header h4 {
    max-width: 100px;
  }

  .list-item-gpu {
    max-width: 150px;
  }
}

@media (max-width: 480px) {
  .rental-hero-title {
    font-size: 28px;
  }

  .rental-hero-desc {
    font-size: 15px;
  }

  .rental-card {
    padding: 16px 14px;
  }

  .rental-card-specs {
    grid-template-columns: 1fr;
  }

  .rental-cta-actions {
    flex-direction: column;
    align-items: center;
  }

  .btn-cta-primary,
  .btn-cta-secondary {
    width: 100%;
    text-align: center;
  }

  .rental-filter-section {
    padding: 10px 12px;
  }

  .filter-tag {
    font-size: 12px;
    padding: 4px 10px;
  }

  .chart-box {
    height: 50px;
    min-width: 50px;
  }

  .list-item-gpu {
    max-width: 120px;
  }

  .spec-value {
    max-width: 60px;
  }

  .period-date {
    font-size: 10px;
  }
}
</style>
